import { queryClient } from '@/plugins/vue-query';
import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth.store';
import { useStorage } from '@vueuse/core';
import type { MaybeRef } from 'vue';
import * as VueGtag from 'vue-gtag';
import { isProduction } from '@/utils/environment.utils';
import { cleeService } from '../services/clee.service';
import type { Project, ProjectBriefStatus } from '../interfaces/project.interface';
import { ProjectSocket } from '../sockets/project.socket';
import {
  ProjectConfigurationTaskStatus,
  type ProjectConfigurationTask,
} from '../interfaces/configurationTask.interface';

interface ProjectState {
  projects: Project[];
  isLoadingProjects: boolean;
  current_project_id?: MaybeRef<number>;
  projectSocket?: ProjectSocket;
  configuration_tasks: ProjectConfigurationTask[];
  last_configuration_task_completed_id: MaybeRef<number | null>;
}

export const useProjectStore = defineStore('project', {
  state: (): ProjectState => ({
    projects: [],
    isLoadingProjects: false,
    current_project_id: useStorage<number>('current_project_id', 0, localStorage),
    configuration_tasks: [],
    last_configuration_task_completed_id: useStorage<number | null>(
      'last_configuration_task_completed_id',
      null,
      sessionStorage,
      {
        serializer: {
          read: (v: string) => (v ? parseInt(v) : null),
          write: (v: number | null) => (v ? v.toString() : ''),
        },
      },
    ),
  }),
  getters: {
    currentProject: (state) => {
      return state.projects.find((project) => project.id === state.current_project_id);
    },
    orderedConfigurationTasks: (state) => {
      return state.configuration_tasks.sort(
        (a, b) => a.configuration_task.order - b.configuration_task.order,
      );
    },
    requiredConfigurationTasks: (state) => {
      const ordered = state.configuration_tasks.sort(
        (a, b) => a.configuration_task.order - b.configuration_task.order,
      );
      return ordered.filter((task) => task.configuration_task.required);
    },
    lastConfigurationTaskCompleted: (state) => {
      return state.configuration_tasks.find(
        (task) => task.id === state.last_configuration_task_completed_id,
      );
    },
    hasPendingRequiredConfigurationTasks: (state) => {
      return state.configuration_tasks
        .filter((task) => task.configuration_task.required)
        .some(
          (task) =>
            task.status === ProjectConfigurationTaskStatus.PENDING ||
            task.status === ProjectConfigurationTaskStatus.IN_PROGRESS,
        );
    },
  },
  actions: {
    async init() {
      await this.getProjects();
      await this.getConfigurationTasks();
      this.subscribeToProjectSocket();
    },
    async getProjects(options: { force?: boolean } = {}) {
      const authStore = useAuthStore();

      if (!authStore.currentAccount?.id) return;

      try {
        this.isLoadingProjects = true;
        const previousProjectId = this.current_project_id;
        const queryOptions = {
          queryKey: ['projects', authStore.currentAccount?.id],
          meta: { persist: true },
          queryFn: () => cleeService.getProjectsByAccountId(authStore.currentAccount?.id as number),
        };
        const { data } = await (options.force
          ? queryClient.fetchQuery(queryOptions)
          : queryClient.ensureQueryData(queryOptions));

        this.projects = data.data;

        const projectFromCurrentOrder = this.projects.find(
          (project) => project.order_id === authStore.user?.current_order_id,
        );

        if (projectFromCurrentOrder?.id) {
          this.current_project_id = projectFromCurrentOrder.id;
          return;
        }

        const previousProjectStillAvailable = this.projects.find(
          (project) => project.id === previousProjectId,
        );

        if (previousProjectStillAvailable?.id) {
          this.current_project_id = previousProjectStillAvailable.id;
          return;
        }

        if (!previousProjectId && this.projects.length === 1) {
          this.current_project_id = this.projects[0]?.id;
        }
      } catch (error: any) {
        if (error?.name === 'CanceledError' || error?.name === 'CancelledError') {
          return;
        }
        console.error(error);
      } finally {
        this.isLoadingProjects = false;
      }
    },
    async setSelectedProject(project_id: number) {
      const authStore = useAuthStore();
      const selectedProject = this.projects.find((project) => project.id === project_id);
      if (!selectedProject?.order_id) throw new Error('Order id is not set.');
      await authStore.setCurrentOrder(selectedProject?.order_id);
      this.current_project_id = project_id;

      if (isProduction()) {
        //Set gtag project id
        VueGtag.set({ project_id: `${project_id}` });
      }
    },
    async updateProjectBriefStatus(
      project_id: number,
      payload: { brief_status: ProjectBriefStatus; brief_comments: string },
    ) {
      await cleeService.updateProjectBriefStatus(project_id, payload);
      await this.refetch();
    },
    async getConfigurationTasks(options: { force?: boolean } = {}) {
      try {
        const projectId = this.current_project_id;
        if (!projectId) return;

        const queryOptions = {
          queryKey: ['project-configuration-tasks', projectId],
          meta: { persist: true },
          queryFn: () => cleeService.getProjectConfigurationTasks(projectId as number),
        };
        const { data } = await (options.force
          ? queryClient.fetchQuery(queryOptions)
          : queryClient.ensureQueryData(queryOptions));

        this.configuration_tasks = data.data;
      } catch (error: unknown) {
        const err = error as { name?: string };
        if (err?.name === 'CanceledError' || err?.name === 'CancelledError') {
          return;
        }
        console.error(error);
      }
    },
    async updateConfigurationTaskStatus(
      project_configuration_task_id: number,
      data: { status: ProjectConfigurationTaskStatus; notes?: string },
    ) {
      if (!this.current_project_id) return;
      await cleeService.updateProjectConfigurationTaskStatus(
        this.current_project_id,
        project_configuration_task_id,
        data,
      );
      await queryClient.invalidateQueries({
        queryKey: ['project-configuration-tasks', this.current_project_id],
      });
      await this.getConfigurationTasks();
    },
    async refetch() {
      const authStore = useAuthStore();
      if (!authStore.currentAccount?.id) return;
      try {
        await queryClient.invalidateQueries({
          queryKey: ['projects', authStore.currentAccount?.id],
        });
        await queryClient.invalidateQueries({
          queryKey: ['project-configuration-tasks', this.current_project_id],
        });
        await queryClient.invalidateQueries({ queryKey: ['landing'] });
        await this.getProjects({ force: true });
        await this.getConfigurationTasks({ force: true });
      } catch (error: unknown) {
        const err = error as { name?: string };
        if (err?.name === 'CanceledError' || err?.name === 'CancelledError') {
          return;
        }
        throw error;
      }
    },
    async clear() {
      const authStore = useAuthStore();
      this.unsubscribeFromProjectSocket();
      await queryClient.removeQueries({ queryKey: ['projects', authStore.currentAccount?.id] });
      this.projects = [];
      this.current_project_id = undefined;
    },
    subscribeToProjectSocket() {
      if (!this.current_project_id || this.projectSocket) return;
      this.projectSocket = new ProjectSocket(this.current_project_id);
    },
    unsubscribeFromProjectSocket() {
      if (!this.projectSocket) return;
      this.projectSocket.close();
      this.projectSocket = undefined;
    },
    setLastConfigurationTaskCompletedId(project_configuration_task_id: number | null) {
      this.last_configuration_task_completed_id = project_configuration_task_id;
    },
  },
});
